import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardList, Clock, CheckCircle2, AlertTriangle, Zap } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import { KPICard } from './OverviewTab';

const COLORS = ['hsl(188, 64%, 38%)', 'hsl(164, 62%, 40%)', 'hsl(38, 95%, 55%)', 'hsl(4, 79%, 55%)', 'hsl(205, 92%, 45%)', 'hsl(280, 60%, 50%)'];

export const OperationsTab = ({ loading, operationsData, workerData }) => {
  if (loading) return <div className="text-center py-12">Loading operations data...</div>;
  if (!operationsData) return null;

  return (
    <div className="space-y-6">
      {/* Operations KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard title="Total Orders" value={operationsData.summary.total_orders} subtitle={`${operationsData.summary.orders_today} today`} icon={ClipboardList} color="blue" />
        <KPICard title="Avg Processing" value={`${operationsData.processing.avg_processing_days} days`} subtitle={`SLA: ${operationsData.processing.sla_target_days} days`} icon={Clock} color="teal" />
        <KPICard title="SLA Compliance" value={`${operationsData.processing.sla_compliance}%`} subtitle="Within target" icon={CheckCircle2} color="emerald" />
        <KPICard title="Backlog" value={operationsData.summary.backlog} subtitle="Orders pending" icon={AlertTriangle} color="amber" />
      </div>

      {/* Order Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Order Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPie>
                <Pie
                  data={operationsData.by_status}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ status, count }) => `${status}: ${count}`}
                >
                  {operationsData.by_status?.map((entry, index) => (
                    <Cell key={`cell-${entry.status}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily Order Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={operationsData.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="created" name="Created" fill="hsl(188, 64%, 85%)" stroke="hsl(188, 64%, 38%)" />
                <Area type="monotone" dataKey="completed" name="Completed" fill="hsl(164, 62%, 85%)" stroke="hsl(164, 62%, 40%)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Worker Productivity */}
      {workerData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Worker Productivity
            </CardTitle>
            <CardDescription>
              {workerData.summary.active_workers} active out of {workerData.summary.total_workers} workers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Worker</TableHead>
                  <TableHead>Orders Handled</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Completion Rate</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workerData.worker_productivity?.slice(0, 10).map((worker, idx) => (
                  <TableRow key={worker.worker_id}>
                    <TableCell>
                      {idx < 3 ? (
                        <Badge className={idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : 'bg-orange-700'}>
                          #{idx + 1}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">#{idx + 1}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{worker.worker_name}</TableCell>
                    <TableCell>{worker.orders_handled}</TableCell>
                    <TableCell>{worker.completed}</TableCell>
                    <TableCell>{worker.completion_rate}%</TableCell>
                    <TableCell className="font-mono">{formatCurrency(worker.revenue_generated)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={worker.productivity_score} className="w-16 h-2" />
                        <span className="text-sm font-semibold">{worker.productivity_score}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
